import PropTypes from "prop-types";
import { useLayoutEffect, useRef } from "react";
import { useCalendarInsights } from "../../programs/program-insights/hooks/useCalendarInsights";
import CalendarInsightsStack from "./CalendarInsightsStack";

/**
 * Renders calendar content + optional Program Insights strip (stacked layout).
 * Uses a render prop so class hosts can attach scrollRef to the grid/week scroller.
 */
function CalendarInsightsSection({
  scope,
  enabled = true,
  from,
  to,
  periodKeys,
  visiblePeriods,
  activeExercises,
  athleteId,
  startingCalendarDay = 0,
  activeUser,
  activeUserToRequest,
  organizationOwner,
  clientConfiguration,
  fromMetric,
  scrollRef: externalScrollRef,
  onInsightsShellActiveChange,
  children,
}) {
  const internalScrollRef = useRef(null);
  const insightsBodyRef = useRef(null);
  const scrollRef = externalScrollRef || internalScrollRef;

  const { insightsInput, storageId, insightsActive } = useCalendarInsights({
    scope,
    enabled,
    from,
    to,
    periodKeys,
    visiblePeriods,
    activeExercises,
    athleteId,
    startingCalendarDay,
  });

  /**
   * Sync viewport shell on `main.Calendar`.
   * Hosts with a React-controlled `main.className` (e.g. edit-mode toggle) should pass
   * `onInsightsShellActiveChange` and apply `Calendar--insights-shell` in that className.
   */
  useLayoutEffect(() => {
    if (onInsightsShellActiveChange) {
      onInsightsShellActiveChange(insightsActive);
      return () => {
        onInsightsShellActiveChange(false);
      };
    }

    const main = insightsBodyRef.current?.closest("main.Calendar");
    if (!(main instanceof HTMLElement)) {
      return undefined;
    }
    if (insightsActive) {
      main.classList.add("Calendar--insights-shell");
    } else {
      main.classList.remove("Calendar--insights-shell");
    }
    return () => {
      main.classList.remove("Calendar--insights-shell");
    };
  }, [insightsActive, onInsightsShellActiveChange]);

  return (
    <div
      ref={insightsBodyRef}
      className={`ProgramEditor-insights-body${
        insightsActive ? " ProgramEditor-insights-body--stacked" : ""
      }`}
    >
      {children(scrollRef)}
      {insightsActive && storageId && (
        <CalendarInsightsStack
          insightsInput={insightsInput}
          storageId={storageId}
          scrollRef={scrollRef}
          fromMetric={fromMetric}
          activeUser={activeUser}
          activeUserToRequest={activeUserToRequest}
          organizationOwner={organizationOwner}
          clientConfiguration={clientConfiguration}
        />
      )}
    </div>
  );
}

CalendarInsightsSection.propTypes = {
  scope: PropTypes.string.isRequired,
  enabled: PropTypes.bool,
  from: PropTypes.string,
  to: PropTypes.string,
  periodKeys: PropTypes.string,
  visiblePeriods: PropTypes.arrayOf(
    PropTypes.shape({
      periodKey: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  activeExercises: PropTypes.arrayOf(PropTypes.object),
  athleteId: PropTypes.string.isRequired,
  startingCalendarDay: PropTypes.number,
  activeUser: PropTypes.object.isRequired,
  activeUserToRequest: PropTypes.object,
  organizationOwner: PropTypes.object,
  clientConfiguration: PropTypes.object,
  fromMetric: PropTypes.func,
  scrollRef: PropTypes.shape({ current: PropTypes.any }),
  onInsightsShellActiveChange: PropTypes.func,
  children: PropTypes.func.isRequired,
};

/** Class for `main.Calendar` viewport shell — keep in sync with Calendar.css. */
export const CALENDAR_INSIGHTS_SHELL_CLASS = "Calendar--insights-shell";

export default CalendarInsightsSection;
